import React from 'react';
import PropTypes from 'prop-types';
import { setupI18n } from '@lingui/core';
import { plural } from '@lingui/macro';
import { I18nProvider } from '@lingui/react';
import { isOverseas } from '@moka-fe/ats.utils';
import moment from 'moment';

import getMessages from './getMessages';

const { enUsMessages, zhCnMessages } = getMessages();

export const LOCALES = {
  EN_US: 'en-US',
  ZH_CN: 'zh-CN',
};

export const HEADHUNTER_LOCALE_SESSION_STORAGE_KEY = 'locale#headhunter';

const CHINESE_LIKE_JOIN = [LOCALES.ZH_CN];
const ENGLISH_LIKE_JOIN = [LOCALES.EN_US];

const CHINESE_LIKE_ENUMERATION = [LOCALES.ZH_CN];
const ENGLISH_LIKE_ENUMERATION = [LOCALES.EN_US];

const PolyglotRenderSeparator = {
  Slash: 'Slash',
  LineBreak: 'LineBreak',
};

/**
 * 提供polyglot系列函数的共用对外接口
 *
 * @param {Array<string>} textArray
 * @param {Object<string, string>} textDict
 * @param {Array<string>} missingLocales
 * @private
 * @return {{ textArray: Array<string>, textDict: Object<string, string>, missingLocales: Array<string>, renderWithSeparator: (PolyglotRenderSeparator) => Array)}}
 */
function polyglotInterface({ textArray, textDict, missingLocales }) {
  return {
    textArray,
    textDict,
    missingLocales,
    renderWithSeparator: (separator = null) => {
      const results = [];

      const size = Object.keys(textDict).length;
      Object.entries(textDict).forEach(([locale, translation], i) => {
        if (translation && translation.length) {
          if (locale === 'default') {
            results.push(<span key={locale}>{translation}</span>);
          } else {
            results.push(
              <span key={locale} lang={locale}>
                {translation}
              </span>
            );
          }
        }
        if (i + 1 < size) {
          switch (separator) {
            case PolyglotRenderSeparator.Slash: {
              results.push(
                <span key={`polyglot-separator-${i}`} className="polyglot-separator">
                  {' '}
                  /{' '}
                </span>
              );
              break;
            }
            case PolyglotRenderSeparator.LineBreak: {
              results.push(<br key={`polyglot-separator-${i}`} className="polyglot-separator" />);
              break;
            }
            default: {
              // nothing
            }
          }
        }
      });
      return results;
    },
  };
}

/**
 * 在@lingui/react基础上做的扩展，通过i18n.utils访问
 */
class I18nUtils {
  constructor(locale, i18n) {
    this.locale = locale;
    this.i18n = i18n;

    this.enumerationMode = null;
    if (CHINESE_LIKE_ENUMERATION.includes(this.locale)) {
      this.enumerationMode = 'chinese';
    } else if (ENGLISH_LIKE_ENUMERATION.includes(this.locale)) {
      this.enumerationMode = 'english';
    } else {
      this.enumerationMode = 'unknown';
    }

    if (CHINESE_LIKE_JOIN.includes(this.locale)) {
      this.joinMode = 'chinese';
    } else if (ENGLISH_LIKE_JOIN.includes(this.locale)) {
      this.joinMode = 'english';
    } else {
      this.joinMode = 'english';
    }
  }

  /**
   * 连接多个words。用于解决有些语言带有空格，有些不带有空格
   *
   * 例如:
   *    ['谷歌', '搜索']在中文locale下返回“谷歌搜索”
   *    ['Google', 'search']在英文locale下返回"Google search"
   *
   * @param {Array<string>} words
   */
  joinWords = (words) => {
    switch (this.joinMode) {
      case 'chinese': {
        return words.join('');
      }
      default: {
        return words.join(' ');
      }
    }
  };

  /**
   * 用分号连接多个句子。用于解决有些语言半角分号带有空格，有些全角不带有空格
   *
   * 例如:
   *    ['谷歌', '搜索']在中文locale下返回“谷歌；搜索”
   *    ['Google', 'search']在英文locale下返回"Google; search"
   *
   * @param {Array<string>} sentenses
   */
  joinSentensesBySemicolon(sentenses) {
    switch (this.joinMode) {
      case 'chinese': {
        return sentenses.join('；');
      }
      default: {
        return sentenses.join('; ');
      }
    }
  }

  /**
   * Format experience. For example, format 7 to '7年'
   *
   * @param {Number} experience
   * @param {Boolean} isExtend - If isExtend is true, it will add '工作经验' to result in some cases
   *
   * @returns {String} formatted experience
   */
  formatExperience = (experience, isExtend = false) => {
    const { i18n } = this;
    let formattedExperience = isExtend
      ? i18n._(plural({ value: experience, one: '#年工作经验', other: '#年工作经验' }))
      : i18n._(plural({ value: experience, one: '#年', other: '#年' }));
    if (isNaN(parseInt(experience))) {
      formattedExperience = null;
    } else if (experience < 0) {
      formattedExperience = i18n.t`在校生`;
    } else if (experience === 0) {
      formattedExperience = i18n.t`应届毕业生`;
    } else if (experience < 1) {
      formattedExperience = isExtend ? i18n.t`1年以下工作经验` : i18n.t`1年以下`;
    } else if (!experience) {
      formattedExperience = null;
    }
    return formattedExperience;
  };

  formatExperienceRange = (startDate, endDate) => {
    const { i18n } = this;

    if (startDate && endDate) {
      const startDateRepr = startDate;
      const endDateTemp = `${endDate}`.replace(/^至/, '');
      const endDateRepr = endDateTemp === '今' ? i18n.t`今` : endDateTemp;
      return i18n.t`${startDateRepr}至${endDateRepr}`;
    }
    return null;
  };

  /**
   * Compose a clause that enumerates through an array of things
   *
   * 如: ['北京', '上海'] => '北京、上海'
   *    ['Beijing', 'Shanghai'] => 'Beijing, Shanghai'
   * @param {Array} items
   */
  enumerate = (items) => {
    const { i18n } = this;
    return items.join(i18n.t`、`);
  };

  /**
   * Compose a clause that is used after an truncated enumeration
   *
   * For instance:
   *
   *   zh-CN: enumerateOmitCounter(3, 1, '人') => 等3人
   *   en-US: enumerateOmitCounter(3, 2, '人') => and 1 other person
   *
   * @param {number} total   - the total count of the list
   * @param {number} count   - the count of the current enumeration
   * @param {string} counter - 一个**中文**量词或计数单位
   */
  enumerateOmitCounter = (total, count, counter) => {
    const { i18n } = this;

    const restCount = total - count;
    switch (counter) {
      // eslint-disable-next-line @moka-fe/lingui-mark
      case '人': {
        switch (this.enumerationMode) {
          case 'chinese': {
            if (total === count) {
              return '';
            }
            return i18n.plural({ value: total, other: '等#人' });
          }
          default: {
            if (!restCount) {
              return '';
            }
            return i18n.plural({ value: restCount, other: '等其他#人' });
          }
        }
      }
      // eslint-disable-next-line @moka-fe/lingui-mark
      case '职位': {
        switch (this.enumerationMode) {
          case 'chinese': {
            if (total === count) {
              return '';
            }
            return i18n.plural({ value: total, other: '#职位' });
          }
          default: {
            if (!restCount) {
              return '';
            }
            return i18n.plural({ value: restCount, other: '等其它#职位' });
          }
        }
      }
    }
    throw new Error(`Unsupported counter: '${counter}' for '${this.locale}`);
  };

  /**
   * 将一个list转换成可读的string
   * 例如：
   *   ['小张'，'小明'，'小王'] -> '小张、小明、小王'
   * 可以指定最大文字长度，如果无法把所有项都列出来，也能优雅地显示，例如：
   *   ['小张'，'小明'，'小王'] -> '小张、小明等3人'
   *
   * @param {Array} items - 列表
   * @param {string} unit - 单位，例如“人”
   * @param {number} [maxLength=20] - 最大文字长度，默认为20个字
   * @returns {string}
   */
  getReadableItems = (items, unit, maxLength = 20) => {
    items = (items || []).filter((x) => !!x && typeof x === 'string');
    const itemCount = items.length;
    if (itemCount <= 0) {
      return '';
    }

    let prevResult = this.enumerate(items);
    if (prevResult.length <= maxLength) {
      return prevResult;
    }

    prevResult = this.joinWords([
      this.enumerate(items.slice(0, 1)),
      this.enumerateOmitCounter(itemCount, 1, unit),
    ]);
    for (let i = 1; i < itemCount; i++) {
      const result = this.joinWords([
        this.enumerate(items.slice(0, i)),
        this.enumerateOmitCounter(itemCount, i, unit),
      ]);
      if (result.length > maxLength) {
        return prevResult;
      } else {
        prevResult = result;
      }
    }

    return prevResult;
  };

  /**
   * 用冒号分隔
   *
   * @param {string} key
   * @param {string} value
   * @return {string}
   */
  formatKeyValueWithColon = (key, value) => {
    const { i18n } = this;
    return i18n.t`${key}：${value}`;
  };

  wrapInParenthesis = (str) => {
    const { i18n } = this;
    return i18n.t`（${str}）`;
  };

  /**
   * Format dateStr base on argument 'format'
   * Or, Convert Date object to default string 'M月D日'. E.g. '9月1日'
   *
   * @param {String}  dateStr e.g. 2016-04-21T03:16:29.000Z
   * @param {String}  format  e.g. YYYY年M月D日 default 'M月D日'
   * @param {Boolean} weekFromNow default true to convert time as a from-now-time
   * @return {String}
   */
  formatDate(dateStr, format, weekFromNow = true) {
    const { i18n } = this;
    const date = moment(dateStr);
    const now = moment();

    if (weekFromNow && now.diff(date, 'week') < 1) {
      return date.locale(i18n.language).fromNow();
    } else {
      return date.locale(i18n.language).format(format ? i18n._(format) : i18n.t`M月D日`);
    }
  }

  /* Format duration(in minutes) into {hour}小时{minutes}分钟
   *  ex: 90 -> 1小时30分钟
   *  @param duration { Number } duration time in minutes
   */
  formatMinuteDuration = (minutes) => {
    const { i18n } = this;
    const m = minutes % 60;
    const h = Math.floor(minutes / 60);

    const mStr = i18n.plural({ value: m, other: '#分钟' });
    if (h > 0) {
      const hStr = i18n.plural({ value: h, other: '#小时' });
      if (m > 0) {
        return this.joinWords([hStr, mStr]);
      }
      return hStr;
    } else {
      return mStr;
    }
  };
}

class I18nUtilsProvider extends React.Component {
  constructor(props, context) {
    const { linguiPublisher } = context;
    const { language: locale, _catalogs: catalogs } = linguiPublisher.i18n;
    linguiPublisher.i18n.utils = new I18nUtils(locale, linguiPublisher.i18n);
    linguiPublisher.i18n.fork = () => getI18nWithUtilsProvider({ locale, catalogs }); // eslint-disable-line no-use-before-define
    linguiPublisher.i18n._polyglot = (key, locales, allowFallback = true) => {
      const textArray = [];
      const textDict = {};
      const missingLocales = [];
      const noDefaultTranslator = () => null;
      for (const _locale of locales) {
        const i18n = _locale === 'default' ? getI18n(null) : getI18n(_locale); // eslint-disable-line no-use-before-define
        const translation =
          _locale === 'default' ? i18n._(key) : i18n._(key, { defaults: noDefaultTranslator });
        if (translation) {
          textArray.push(translation);
          textDict[_locale] = translation;
        } else {
          missingLocales.push(_locale);
        }
      }
      if (!textArray.length && !locales.includes('default') && allowFallback) {
        const i18n = getI18n(null); // eslint-disable-line no-use-before-define
        textArray.push(i18n._(key));
        textDict.default = i18n._(key);
      }
      return polyglotInterface({ textArray, textDict, missingLocales });
    };
    super(props, context);
  }

  render() {
    return this.props.children;
  }
}

I18nUtilsProvider.contextTypes = {
  linguiPublisher: PropTypes.object.isRequired,
};

/**
 * @param {string} locale
 * @param {object} catalogs
 */
function getI18nWithUtilsProvider({ locale, catalogs }) {
  return (props) => {
    return (
      <I18nProvider language={locale} catalogs={catalogs}>
        <I18nUtilsProvider>{props.children}</I18nUtilsProvider>
      </I18nProvider>
    );
  };
}

let enUsI18n;
const getEnUsI18n = () => {
  if (!enUsI18n) {
    enUsI18n = setupI18n({
      catalogs: {
        [LOCALES.EN_US]: enUsMessages,
      },
      language: LOCALES.EN_US,
    });

    enUsI18n.utils = new I18nUtils(LOCALES.EN_US, enUsI18n);
  }
  return enUsI18n;
};

let zhCnI18n;
const getZhCnI18n = () => {
  if (!zhCnI18n) {
    zhCnI18n = setupI18n({
      catalogs: {
        [LOCALES.ZH_CN]: zhCnMessages,
      },
      language: LOCALES.ZH_CN,
    });

    zhCnI18n.utils = new I18nUtils(LOCALES.ZH_CN, zhCnI18n);
  }
  return zhCnI18n;
};

export function getDefaultLocale() {
  let locale = '';
  try {
    if (!locale && typeof document !== 'undefined') {
      locale = document.documentElement.lang;
    }

    if (!locale && typeof navigator !== 'undefined') {
      if (/en/i.test(navigator.language)) {
        locale = LOCALES.EN_US;
      } else if (/zh/i.test(navigator.language)) {
        locale = LOCALES.ZH_CN;
      }
    }

    if (!locale) {
      locale = LOCALES.ZH_CN;
    }
  } catch (e) {
    locale = LOCALES.ZH_CN;
  }

  return locale;
}

/**
 * 获取i18n实例
 *
 * 后端使用时,locale必填
 * 前段使用时,locale可选
 *
 * @param {string} [locale] - 如果不提供,则尝试从document中获取
 * @return { import("@lingui/core").I18n & { utils: I18nUtils } } i18n instance
 */
export function getI18n(locale) {
  locale = locale || getDefaultLocale();

  switch (locale) {
    case LOCALES.EN_US:
      return getEnUsI18n();
    case LOCALES.ZH_CN:
      return getZhCnI18n();
    default:
      return getZhCnI18n();
  }
}

// ///////////////////////////////////////////////////////////////////////////////////

/**
 * 这一组用于在浏览器中，设置I18n对象等
 */
let browserI18n = null;
export function getBrowserI18nWithUtilsProvider() {
  if (typeof window !== 'object') {
    return console.error(
      new Error(
        'Calling getBrowserI18nWithUtilsProvider in a non-browser environment is a mistake.'
      )
    );
  }

  const locale = getDefaultLocale();

  const catalogs = {
    [LOCALES.EN_US]: enUsMessages,
    [LOCALES.ZH_CN]: zhCnMessages,
  };

  if (!browserI18n) {
    window.i18n = setupI18n({
      catalogs,
      language: locale,
    });
    window.i18n.utils = new I18nUtils(locale, window.i18n);
    browserI18n = window.i18n;
  }

  return getI18nWithUtilsProvider({ locale, catalogs });
}

if (typeof window === 'object') {
  getBrowserI18nWithUtilsProvider();
}

export const getClientLocalLang = (language) => {
  switch (language) {
    case 'zh-CN':
      return 'zh_CN';
    case 'en-US':
      return 'en_US';
    default:
      return 'zh_CN';
  }
};

/**
 * 为什么会有这个方法？
 * 因为登录页的路由时/headhunters, 登录过后的首页也是/headhunters，所以不能在getDefaultLocale中直接添加判断
 */
export const getHeadhunterDefaultLoginLocale = () => {
  return (
    localStorage.getItem(HEADHUNTER_LOCALE_SESSION_STORAGE_KEY) ||
    (isOverseas ? LOCALES.EN_US : '') ||
    getDefaultLocale()
  );
};
